import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { PaletteItem } from './ReportCanvas';
import { useComponentRegistry } from './registry-context';

/**
 * Searchable grid of draggable component tiles, driven by the active
 * component registry. Must be rendered inside the same DndContext as the
 * canvas it feeds. `onAdd` also adds a tile's component on click; `compact`
 * fits the tiles to a narrow sidebar.
 */
export function ReportPalette({
  onAdd,
  compact = false,
}: {
  onAdd?: (componentType: string) => void;
  compact?: boolean;
} = {}) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  const registry = useComponentRegistry();

  const entries = Object.values(registry);
  const filtered = search.trim()
    ? entries.filter((def) =>
        def.label.toLowerCase().includes(search.trim().toLowerCase()),
      )
    : entries;

  return (
    <div className="space-y-3 p-3">
      <div className="space-y-1">
        <p className="text-xs font-medium text-foreground">
          <T>Components</T>
        </p>
        <p className="text-xs text-muted-foreground">
          {onAdd ? (
            <T context="report palette hint with click to add">
              Drag a component onto the canvas, or click it to add it at the
              bottom.
            </T>
          ) : (
            <T>Drag a component onto the canvas to add it.</T>
          )}
        </p>
      </div>
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={gt('Search…')}
          className="h-7 pl-7 text-xs"
        />
      </div>
      <div
        className={
          compact
            ? 'grid grid-cols-2 gap-2'
            : 'grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-2'
        }
      >
        {filtered.map((def) => {
          const Preview = def.Preview;
          return (
            <PaletteItem
              key={def.type}
              componentType={def.type}
              label={def.label}
              preview={<Preview />}
              onClick={onAdd && (() => onAdd(def.type))}
            />
          );
        })}
        {filtered.length === 0 && (
          <p className="col-span-full py-4 text-center text-xs text-muted-foreground">
            <T>No components match your search.</T>
          </p>
        )}
      </div>
    </div>
  );
}
